import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { HeartHandshake, HandCoins, Users, Briefcase, Building2, Ticket, Check, ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react';
import { getStoredInvite } from '../utils/invite';
import PublicShell, { PublicShellStatus } from './public/PublicShell';
import { REGISTRATION_STEPS } from './public/registrationSteps';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Copy + icon per registration type, keyed on the entity name from the API.
const TYPE_META = {
    'non-profit': { icon: HeartHandshake, description: 'A nonprofit organization participating in the event or the Donor Circle.' },
    nonprofit: { icon: HeartHandshake, description: 'A nonprofit organization participating in the event or the Donor Circle.' },
    donor: { icon: HandCoins, description: 'An individual or family office giving through the Donor Circle.' },
    philanthropist: { icon: HandCoins, description: 'An individual or family office giving through the Donor Circle.' },
    sponsor: { icon: Building2, description: 'A firm sponsoring the event.' },
    'advisory board': { icon: Users, description: 'A member of the Funds4Charity advisory board.' },
    staff: { icon: Briefcase, description: 'Event staff and organizers.' },
    guest: { icon: Ticket, description: 'Invited guests attending the event.' },
};

const metaFor = (name = '') => TYPE_META[name.trim().toLowerCase()] || { icon: Ticket, description: null };

const RegistrationTypeSelection = () => {
    const { eventNameId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [eventEntities, setEventEntities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEntityId, setSelectedEntityId] = useState('');
    const [entityError, setEntityError] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [eventRegistrationGuid, setEventRegistrationGuid] = useState('');
    const [invitePreselected, setInvitePreselected] = useState(false);
    const userEmail = localStorage.getItem('userEmail') || '';

    useEffect(() => {
        const storedGuid = localStorage.getItem('eventRegistrationGuid');
        if (!storedGuid) {
            navigate(`/event/${eventNameId}/registration`);
            return;
        }
        setEventRegistrationGuid(storedGuid);

        const fetchEventData = async () => {
            try {
                const [eventResponse, entitiesResponse] = await Promise.all([
                    axios.get(`${API_BASE_URL}/public/events/${eventNameId}`),
                    axios.get(`${API_BASE_URL}/public/event-entities/${eventNameId}`)
                ]);

                setEvent(eventResponse.data);
                setEventEntities(entitiesResponse.data);

                // Nominated nonprofits arrive via an invite link: preselect the Non-Profit type for them
                const invite = getStoredInvite();
                if (invite?.event_name_id === eventNameId && invite?.nonprofit_event_entity_id &&
                    entitiesResponse.data.some((e) => e.EventEntityId === invite.nonprofit_event_entity_id)) {
                    setSelectedEntityId(String(invite.nonprofit_event_entity_id));
                    setInvitePreselected(true);
                }
            } catch (err) {
                setError('Failed to load event data');
            } finally {
                setLoading(false);
            }
        };

        if (eventNameId) {
            fetchEventData();
        }
    }, [eventNameId, navigate]);

    const handleSelect = (value) => {
        setSelectedEntityId(String(value));
        setEntityError('');
        setSubmitError('');
    };

    const handleNext = async (e) => {
        e?.preventDefault();
        if (!selectedEntityId) {
            setEntityError('Please choose how you’re attending');
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');
        try {
            await axios.put(`${API_BASE_URL}/public/event-registrations/${eventRegistrationGuid}`, {
                event_entity_id: parseInt(selectedEntityId, 10)
            });
            navigate(`/event/${eventNameId}/registration/form`);
        } catch (err) {
            setSubmitError('We couldn’t save your selection. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <PublicShellStatus><span className="pshell-spinner" aria-hidden="true" /> Loading registration options…</PublicShellStatus>;
    }

    if (error || !event) {
        return <PublicShellStatus error>{error || 'Event not found'}</PublicShellStatus>;
    }

    return (
        <PublicShell
            event={event}
            steps={REGISTRATION_STEPS}
            currentStep={1}
            stepsTitle="Registering takes a few minutes"
            eyebrow="Registration"
            title="How are you attending?"
            subtitle="Choose the option that best describes you. This decides which questions we ask next."
            footer={<>Already registered? <Link to={`/login?event=${encodeURIComponent(eventNameId)}`}>Sign in</Link></>}
        >
            {userEmail && (
                <div className="pshell-kv">
                    <span>Registering <span className="pshell-kv-value">{userEmail}</span></span>
                    <Link to={`/event/${eventNameId}/registration`} className="pshell-inline-btn">Change</Link>
                </div>
            )}

            {invitePreselected && (
                <div className="pshell-alert info">
                    <HeartHandshake size={16} aria-hidden="true" />
                    <div>We pre-selected <strong>Non-Profit</strong> based on your nomination. You can still change it.</div>
                </div>
            )}
            {submitError && (
                <div className="pshell-alert error" role="alert">
                    <AlertCircle size={16} aria-hidden="true" />
                    <div>{submitError}</div>
                </div>
            )}

            <form onSubmit={handleNext} noValidate>
                <ul className="pshell-options" role="radiogroup" aria-label="Registration type" aria-describedby={entityError ? 'type-error' : undefined}>
                    {eventEntities.map((entity) => {
                        const { icon: Icon, description } = metaFor(entity.Name);
                        const selected = String(entity.EventEntityId) === selectedEntityId;
                        return (
                            <li key={entity.EventEntityId}>
                                <label className={`pshell-option ${selected ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="registration-type"
                                        value={entity.EventEntityId}
                                        checked={selected}
                                        onChange={() => handleSelect(entity.EventEntityId)}
                                        disabled={isSubmitting}
                                    />
                                    <span className="pshell-option-icon"><Icon size={18} aria-hidden="true" /></span>
                                    <span className="pshell-option-body">
                                        <span className="pshell-option-title">{entity.Name}</span>
                                        {description && <span className="pshell-option-desc">{description}</span>}
                                    </span>
                                    <span className="pshell-option-check" aria-hidden="true"><Check size={12} strokeWidth={3} /></span>
                                </label>
                            </li>
                        );
                    })}
                </ul>
                {entityError && <div id="type-error" className="pshell-error" role="alert" style={{ marginTop: 10 }}>{entityError}</div>}

                <div className="pshell-actions">
                    <Link to={`/event/${eventNameId}/registration`} className="pshell-btn ghost">
                        <ArrowLeft size={16} aria-hidden="true" /> Back
                    </Link>
                    <button type="submit" className="pshell-btn primary" disabled={!selectedEntityId || isSubmitting}>
                        {isSubmitting ? 'Saving…' : <>Continue <ArrowRight size={16} aria-hidden="true" /></>}
                    </button>
                </div>
            </form>
        </PublicShell>
    );
};

export default RegistrationTypeSelection;
